<template>
  <Listbox
    as="div"
    class="relative flex flex-col gap-4"
    :class="{
      'opacity-60': disabled,
    }"
    :modelValue="modelValue"
    @update:modelValue="emit('update:modelValue', $event)"
    :disabled="disabled"
    :multiple="multiple"
  >
    <ListboxButton
      ref="dropdownSelectorButton"
      @click.stop="openThrottled ? closeDropdown() : openDropdown()"
      :class="{
        'relative w-full cursor-pointer rounded-sm border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500 sm:text-sm':
          props.selectorStyle === 'dropdown',
        '!cursor-not-allowed hover:text-gray-900 group': disabled,
        'text-gray-900 font-normal': selectorStyle === 'dropdown',
      }"
      class="flex flex-nowrap items-center transition max-w-full text-gray-500 font-medium"
    >
      <span class="flex flex-nowrap items-center max-w-full">
        <MapPinIcon
          v-if="selectorStyle !== 'dropdown'"
          class="h-5 w-5 text-gray-500 group-hover:text-gray-900"
        />
        <span
          :class="{ 'ml-2': selectorStyle !== 'dropdown' }"
          class="flex-1 whitespace-nowrap overflow-hidden text-ellipsis"
        >
          {{ buttonLabel }}
        </span>
        <span
          v-if="selectorStyle === 'dropdown'"
          class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2"
        >
          <ChevronDownIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
        </span>
      </span>
    </ListboxButton>

    <!-- Dropdown -->
    <transition
      leave-active-class="transition ease-in duration-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <ListboxOptions
        ref="selectorDropdown"
        v-if="openThrottled"
        static
        :class="[
          `left-[-${horizontalLabelWidth}]`,
          {
            'w-full': selectorStyle === 'dropdown',
          },
        ]"
        class="z-20 mt-2 flex flex-col rounded-md bg-white border-gray-300 border shadow-lg focus:outline-none text-sm fixed"
        :style="placementStyles"
      >
        <ButtonSelector
          :options="allocationTypes"
          v-model="selectedAllocationType"
          :standalone="false"
          :showCount="multiple"
        />

        <FilterSearchInput
          ref="filterSearchInput"
          v-model="searchQuery"
          :placeholder="t('components.inputs.search')"
        />

        <div class="max-h-56 overflow-auto">
          <template v-if="locationOptions?.length">
            <tree
              :spaces="locationOptions"
              :value="treeValue"
              :single="!multiple"
              :is-searching="searchQuery?.length > 0"
              @checked="handleLocationSelected"
              :disable-ungrouped="props.disableUngrouped"
              :disable-groups="props.disableGroups"
              :disable-tenants="props.disableTenants"
              :show-identifier="props.showIdentifier"
            />
          </template>
          <div v-else class="grow px-3 py-2 text-gray-500">
            {{ t("components.no_result") }}
          </div>
        </div>
      </ListboxOptions>
    </transition>
  </Listbox>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import ButtonSelector from "@/Tailwind/Components/Input/ButtonSelector.vue";
import Tree from "@/Tailwind/Components/Tree/Tree";
import { useI18n } from "vue-i18n";
import { useLocations } from "@/Tailwind/Mixins/useLocations";
import { Listbox, ListboxButton, ListboxOptions } from "@headlessui/vue";
import { MapPinIcon } from "@heroicons/vue/24/outline";
import { ChevronDownIcon } from "@heroicons/vue/20/solid";
import FilterSearchInput from "@/Tailwind/Components/Input/Filters/FilterSearchInput";
import { useReactiveRendering } from "@/Tailwind/Mixins/useReactiveRendering";
import { onClickOutside, refThrottled } from "@vueuse/core";
import { usePreloadStore } from "@/Tailwind/Stores/preload";

const props = defineProps({
  modelValue: Object, // Location object
  tenants: Array, // Array of tenants to include in the dropdown
  horizontalLabelWidth: {
    type: String,
    default: "0rem",
  },
  placeholder: String,
  disabled: Boolean,
  selectorStyle: String,
  disableUngrouped: Boolean,
  disableGroups: Boolean,
  disableTenants: Boolean,
  multiple: Boolean,
  showIdentifier: Boolean,
  spaces: Array,
  assets: Array,
});

const emit = defineEmits(["update:modelValue"]);

const selectorDropdown = ref(null);
const open = ref(false);
const openThrottled = refThrottled(open, 100, true, false);

const openDropdown = () => {
  if (openThrottled.value !== open.value) {
    return;
  }

  open.value = true;
};

const closeDropdown = () => {
  if (openThrottled.value !== open.value) {
    return;
  }

  open.value = false;
};

onClickOutside(selectorDropdown, () => {
  if (!open.value) {
    return;
  }

  closeDropdown();
});

const { t } = useI18n();

const { findLocationRecursive } = useLocations();

const allocationTypes = computed(() => [
  {
    key: "spaces",
    label: t("components.location_selector.spaces"),
    count: counts.value.spaces,
  },
  {
    key: "assets",
    label: t("components.location_selector.assets"),
    count: counts.value.assets,
  },
]);

const selectedAllocationType = ref("spaces");
const filterSearchInput = ref(null);

const treeValue = computed(() => {
  if (!props.modelValue) {
    return [];
  }

  if (props.multiple) {
    return props.modelValue;
  }

  return [props.modelValue.id];
});

watch(selectedAllocationType, () => {
  if (!props.multiple) {
    emit("update:modelValue", null);
  }

  filterSearchInput.value?.focus();
  filterSearchInput.value?.clear();
});

const handleLocationSelected = (value) => {
  if (!props.multiple) {
    closeDropdown();
  }

  emit("update:modelValue", value);
};

const searchQuery = ref("");

const hierarchies = computed(() => {
  const preloadStore = usePreloadStore();
  let filteredSpaceHierarchy = preloadStore.locations;
  let filteredAssetHierarchy = preloadStore.assets;

  // Fallback to spaces and assets supplied directly from the parent component.
  if (filteredAssetHierarchy === null && filteredSpaceHierarchy === null) {
    return {
      spaces: props.spaces,
      assets: props.assets,
    };
  }

  if (props.tenants?.length > 0) {
    const tenantIds = props.tenants.map((tenant) =>
      typeof tenant === "object" ? tenant.id : tenant
    );

    filteredSpaceHierarchy = filteredSpaceHierarchy.filter((hierarchy) =>
      tenantIds.includes(hierarchy.id)
    );

    if (filteredSpaceHierarchy.length === 1) {
      filteredSpaceHierarchy = filteredSpaceHierarchy[0].children.map((el) => ({
        ...el,
        tenantId: filteredSpaceHierarchy[0].id,
      }));
    }

    filteredAssetHierarchy = filteredAssetHierarchy.filter((hierarchy) =>
      tenantIds.includes(hierarchy.id)
    );

    if (filteredAssetHierarchy.length === 1) {
      filteredAssetHierarchy = filteredAssetHierarchy[0].children.map((el) => ({
        ...el,
        tenantId: filteredAssetHierarchy[0].id,
      }));
    }
  }

  return {
    spaces: filteredSpaceHierarchy,
    assets: filteredAssetHierarchy,
  };
});

const locationOptions = computed(() => {
  switch (selectedAllocationType.value) {
    case "spaces":
    default:
      if (!searchQuery.value) {
        return hierarchies.value.spaces;
      }

      return performSearch(hierarchies.value.spaces, searchQuery.value);

    case "assets":
      if (!searchQuery.value) {
        return hierarchies.value.assets;
      }

      return performSearch(hierarchies.value.assets, searchQuery.value);
  }
});

const buttonLabel = computed(() => {
  if (!props.modelValue) {
    return props.placeholder ?? t("components.buttons.make_selection");
  }

  if (Array.isArray(props.modelValue)) {
    if (props.modelValue.length > 1) {
      return props.modelValue.length + " " + t("components.selected");
    }

    let location = findLocationRecursive(
      hierarchies.value.spaces,
      props.modelValue[0]
    );

    if (!location) {
      location = findLocationRecursive(
        hierarchies.value.assets,
        props.modelValue[0]
      );
    }

    return location ? location.name : t("components.inputs.expansion.no_value");
  }

  return props.modelValue.name;
});

const counts = computed(() => {
  const counts = {
    spaces: 0,
    assets: 0,
  };

  if (!props.modelValue || !props.multiple) {
    return counts;
  }

  props.modelValue?.forEach((id) => {
    const space = findLocationRecursive(hierarchies.value.spaces, id);

    if (space) {
      counts.spaces++;
      return;
    }

    const asset = findLocationRecursive(hierarchies.value.assets, id);

    if (asset) {
      counts.assets++;
    }
  });

  return counts;
});

function performSearch(root, query) {
  const result = [];
  root.forEach((node) => {
    let children;

    if (
      node.name.toLowerCase().includes(query.toLowerCase()) ||
      node.identifier?.toLowerCase().includes(query.toLowerCase())
    ) {
      // Also filter node if it has children that match the query
      children = performSearch(node.children || [], query);

      result.push({ ...node, children });

      return;
    }

    children = performSearch(node.children || [], query);

    if (children.length) {
      result.push(Object.assign({}, node, { children }));
    }
  });

  return result;
}

const dropdownSelectorButton = ref(null);

const { placementStyles } = useReactiveRendering(
  dropdownSelectorButton,
  null,
  props.scrollContainer
);
</script>
